import { useNavigate } from 'react-router-dom';
import { IconCamera } from '@tabler/icons-react';
import { RIDES_VERIFICATION_STATUS, RIDES_VERIFICATION_TYPE } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { findVerification, useDriverProfile, useMyVerifications, useRiderProfile } from '@/lib/me';
import { useMyPhoto } from '@/lib/photo';
import { photoRules, useCopy } from '@/lib/settings';
import { Pill } from '@/components/rides/Pill';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import '@/styles/member.css';

/**
 * Top of Account: the member's photo (with a camera button that opens the photo row), name,
 * email and a few honest badges (ID checked, Driver, Rider). Badges only appear once their data
 * has loaded.
 */
export function ProfileHeader() {
  const navigate = useNavigate();
  const { user, getUserDisplayName } = useEnrichedAuth();
  const { settings } = useCopy();
  const photo = useMyPhoto();
  const rider = useRiderProfile();
  const driver = useDriverProfile();
  const verifications = useMyVerifications();
  const name = getUserDisplayName() || `${user?.firstName ?? ''} ${user?.surname ?? ''}`.trim();
  const id = verifications.isSuccess ? findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId) : undefined;
  const photosOn = photoRules(settings).enabled;

  return (
    <header className="rm-profile rm-rise">
      <div className="pic">
        <span className="rm-avatar-ring"><PersonAvatar src={photo.url} name={name} size={96} alt="Your profile photo" /></span>
        {photosOn && (
          <button
            type="button"
            className="cam"
            aria-label={photo.hasPhoto ? 'Change your photo' : 'Add a photo'}
            onClick={() => navigate({ hash: '#photo' }, { replace: true })}
          >
            <IconCamera size={20} aria-hidden="true" />
          </button>
        )}
      </div>
      <h1>{name || 'Your account'}</h1>
      {user?.email && <p className="mail">{user.email}</p>}
      <div className="badges">
        {id?.statusID === RIDES_VERIFICATION_STATUS.Verified && <Pill kind="ok">ID checked</Pill>}
        {id?.statusID === RIDES_VERIFICATION_STATUS.Pending && <Pill kind="warn">ID check waiting</Pill>}
        {driver.data && <Pill kind="info" dot={false}>Driver</Pill>}
        {rider.data && <Pill kind="neutral" dot={false}>Rider</Pill>}
      </div>
    </header>
  );
}
